import React, { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabaseClient';
import { ScanLine } from 'lucide-react';

// ── Field Assist scan wall ───────────────────────────────────────────────────
// The big screen's resting state on the FIELD ASSIST scene only: one
// appliance photo per joined attendee, their name underneath, so each person
// points Field Assist at THEIR tile and scans it live (owner, 3 Oct). The
// assignment is deterministic and storage-free — attendee №N gets pool image
// N (wrapping) — so it is stable all night and survives refreshes. Late
// joiners appear on the next 4s tick. The pool is the admin-managed folder
// below (Product Demos → Appliance scan wall); an empty pool shows the
// presenter a quiet instruction instead of a blank screen.
export const SCAN_WALL_BUCKET = 'message-attachments';
export const SCAN_WALL_PATH = 'field-assist-scan';

const POLL_MS = 4000;

/** The pool, sorted by filename so assignment order is deliberate. */
export async function fetchScanWallImages() {
  try {
    const { data, error } = await supabase.storage.from(SCAN_WALL_BUCKET).list(SCAN_WALL_PATH, { limit: 100 });
    if (error) return [];
    return (data || [])
      .filter((f) => f.name && !f.name.startsWith('.'))
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((f) => {
        const { data: pub } = supabase.storage.from(SCAN_WALL_BUCKET).getPublicUrl(`${SCAN_WALL_PATH}/${f.name}`);
        const stamp = encodeURIComponent(f.updated_at || f.created_at || '');
        return { name: f.name, url: pub?.publicUrl ? `${pub.publicUrl}?v=${stamp}` : null };
      })
      .filter((f) => f.url);
  } catch {
    return [];
  }
}

const firstName = (full) => (full || '').trim().split(/\s+/)[0] || 'Guest';

const FieldAssistScanWall = ({ sessionId }) => {
  const [attendees, setAttendees] = useState([]);
  const [pool, setPool] = useState(null); // null = loading

  useEffect(() => {
    let stale = false;
    fetchScanWallImages().then((imgs) => { if (!stale) setPool(imgs); });
    return () => { stale = true; };
  }, [sessionId]);

  useEffect(() => {
    if (!sessionId) return undefined;
    let stop = false;
    const load = async () => {
      try {
        const { data } = await supabase
          .from('demo_nda_signatures')
          .select('id, full_name, attendee_no')
          .eq('session_id', sessionId)
          .order('attendee_no', { ascending: true });
        if (!stop && Array.isArray(data)) setAttendees(data);
      } catch { /* keep last known list */ }
    };
    load();
    const id = setInterval(load, POLL_MS);
    return () => { stop = true; clearInterval(id); };
  }, [sessionId]);

  const tiles = attendees.map((a, i) => ({
    key: a.id,
    name: firstName(a.full_name),
    image: pool && pool.length > 0 ? pool[((a.attendee_no ?? i + 1) - 1 + pool.length * 1000) % pool.length] : null,
  }));

  // Grid stays scannable from seats: up to 12 tiles scale; beyond that the
  // wall scrolls rather than shrinking tiles into unscannable stamps.
  const cols = tiles.length <= 2 ? tiles.length || 1 : tiles.length <= 6 ? 3 : 4;
  // The WHOLE image must stay visible — a retailer screenshot's product title
  // is part of what the app reads, and the square crop was slicing it off
  // (owner, 3 Oct). Images render uncropped (object-contain) inside a height
  // budget derived from the rows on screen, so tile + name always share the
  // viewport instead of one screenshot swallowing it.
  const rows = Math.max(1, Math.ceil(tiles.length / cols));
  const imgMaxHeight = `clamp(160px, calc((100vh - 400px) / ${rows}), 56vh)`;

  return (
    <div className="absolute inset-0 overflow-y-auto bg-slate-950 text-white">
      <div className="flex min-h-full flex-col items-center justify-center gap-6 px-10 pt-16 pb-28 text-center">
        <p className="flex items-center gap-2 text-[13px] font-semibold uppercase tracking-[0.35em]" style={{ color: 'rgba(255,255,255,0.7)' }}>
          <ScanLine className="h-4 w-4 text-primary" /> Field Assist · scan your appliance
        </p>
        <h1 className="max-w-3xl text-2xl font-semibold leading-tight sm:text-4xl" style={{ color: '#ffffff' }}>
          Find your name. Point Field Assist at your appliance.
        </h1>

        {pool !== null && pool.length === 0 ? (
          <p className="max-w-xl text-lg" style={{ color: 'rgba(255,255,255,0.75)' }}>
            No appliance photos yet — add them on Product Demos &rarr; Appliance scan wall.
          </p>
        ) : tiles.length === 0 ? (
          <p className="max-w-xl text-lg" style={{ color: 'rgba(255,255,255,0.75)' }}>
            Waiting for attendees to join&hellip;
          </p>
        ) : (
          <div
            className={`grid w-full gap-5 ${tiles.length === 1 ? 'max-w-3xl' : 'max-w-6xl'}`}
            style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}
          >
            {tiles.map((tile) => (
              <div key={tile.key} className="flex flex-col items-center gap-2 rounded-3xl border border-white/10 bg-white p-3 shadow-2xl">
                {tile.image ? (
                  <img
                    src={tile.image.url}
                    alt={`Appliance for ${tile.name} to scan`}
                    className="w-auto max-w-full rounded-2xl object-contain"
                    style={{ maxHeight: imgMaxHeight }}
                  />
                ) : (
                  <div className="w-full animate-pulse rounded-2xl bg-slate-200" style={{ height: imgMaxHeight }} aria-hidden="true" />
                )}
                <p className="pb-1 text-base font-semibold text-slate-900 sm:text-lg">{tile.name}</p>
              </div>
            ))}
          </div>
        )}

        <p className="text-xs uppercase tracking-[0.25em]" style={{ color: 'rgba(255,255,255,0.45)' }}>
          Open Field Assist on your phone · ask about what you see
        </p>
      </div>
    </div>
  );
};

export default FieldAssistScanWall;
